/**
 * EO-01 — EventChanges: Changes tab content for EventImagine
 * Renders: list of changes + Record Change modal
 * No hardcoded change types — all loaded from /api/events/:id/changes/config
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/services/api";
import {
  Plus, Loader2, AlertTriangle, CheckCircle2, Clock,
  ChevronRight, Filter, RefreshCw, GitPullRequest,
  Zap, CalendarClock, MapPin, Settings, ArrowRight
} from "lucide-react";
import { toast } from "sonner";

// ── Severity / status colors ──────────────────────────────────────────────────
const SEVERITY_STYLE = {
  CRITICAL: "bg-[#FCEBEC] text-[#BA0517] border border-[#BA0517]/20",
  HIGH:     "bg-[#FFF4E5] text-[#8C4B00] border border-[#FE9339]/30",
  MEDIUM:   "bg-[#FFF4E5] text-[#8C4B00] border border-[#FE9339]/30",
  LOW:      "bg-[#FAFAFA] text-[#706E6B] border border-[#C9C9C9]",
};

const STATUS_STYLE = {
  PROPOSED:           "bg-[#FAFAFA] text-[#706E6B] border border-[#C9C9C9]",
  IMPACT_ASSESSED:    "bg-[#EEF4FF] text-[#0176D3] border border-[#0176D3]/20",
  AWAITING_DECISION:  "bg-[#F6EEFE] text-[#6832B8] border border-[#6832B8]/20",
  AWAITING_APPROVAL:  "bg-[#FFF4E5] text-[#8C4B00] border border-[#FE9339]/30",
  APPROVED:           "bg-[#EAF5EE] text-[#2E844A] border border-[#2E844A]/20",
  IMPLEMENTING:       "bg-[#EEF4FF] text-[#0176D3] border border-[#0176D3]/20",
  IMPLEMENTED:        "bg-[#EAF5EE] text-[#2E844A] border border-[#2E844A]/20",
  VERIFIED:           "bg-[#EAF5EE] text-[#2E844A] border border-[#2E844A]/30",
  CLOSED:             "bg-white text-[#706E6B] border border-[#E5E5E5]",
  REJECTED:           "bg-[#FCEBEC] text-[#BA0517] border border-[#BA0517]/20",
  CANCELLED:          "bg-[#FAFAFA] text-[#706E6B] border border-[#C9C9C9]",
};

const CHANGE_TYPE_ICON = {
  GUEST_COUNT: "👥",
  EVENT_DATE:  "📅",
  VENUE:       "📍",
  EVENT_TIME:  "🕐",
  MENU:        "🍽",
  DESIGN:      "🎨",
  SCHEDULE:    "📋",
  LOGISTICS:   "🚛",
  OTHER:       "📌",
};

const STATUS_FILTER_OPTIONS = [
  { value: "", label: "All Changes" },
  { value: "PROPOSED", label: "Proposed" },
  { value: "IMPACT_ASSESSED", label: "Impact Assessed" },
  { value: "AWAITING_DECISION", label: "Awaiting Decision" },
  { value: "AWAITING_APPROVAL", label: "Awaiting Approval" },
  { value: "APPROVED", label: "Approved" },
  { value: "IMPLEMENTING", label: "Implementing" },
  { value: "IMPLEMENTED", label: "Implemented" },
  { value: "CLOSED", label: "Closed" },
];

export default function EventChanges({ eventId }) {
  const navigate = useNavigate();
  const [changes, setChanges] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [showModal, setShowModal] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cRes, cfgRes] = await Promise.all([
        api.get(`/events/${eventId}/changes`, { params: { status: statusFilter || undefined } }),
        api.get(`/events/${eventId}/changes/config`),
      ]);
      setChanges(cRes.data.changes || []);
      setConfig(cfgRes.data);
    } catch {
      toast.error("Failed to load changes");
    } finally {
      setLoading(false);
    }
  }, [eventId, statusFilter]);

  useEffect(() => { load(); }, [load]);

  // Count by severity for summary strip
  const criticalCount = changes.filter(c => c.severity === "CRITICAL").length;
  const highCount     = changes.filter(c => c.severity === "HIGH").length;
  const openCount     = changes.filter(c =>
    !["CLOSED", "REJECTED", "CANCELLED"].includes(c.status)
  ).length;

  return (
    <div className="space-y-4" data-testid="event-changes-tab">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-sm font-semibold text-[#181818]">Event Changes</h2>
          <p className="text-xs text-[#706E6B] mt-0.5">
            Structural changes with cross-thread impact assessment
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="p-2 text-[#706E6B] hover:text-[#181818] border border-[#C9C9C9] rounded-lg">
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
          <button
            data-testid="record-change-btn"
            onClick={() => setShowModal(true)}
            className="flex items-center gap-1.5 px-3 py-2 bg-[#0176D3] text-white text-xs font-semibold rounded-lg hover:opacity-90 transition-opacity"
          >
            <Plus className="w-3.5 h-3.5" />
            Record Change
          </button>
        </div>
      </div>

      {/* Summary strip */}
      {changes.length > 0 && (
        <div className="flex gap-3 flex-wrap">
          <SummaryChip label="Open" value={openCount} color="text-[#444444]" />
          {criticalCount > 0 && <SummaryChip label="Critical" value={criticalCount} color="text-[#BA0517]" />}
          {highCount > 0 && <SummaryChip label="High" value={highCount} color="text-[#8C4B00]" />}
        </div>
      )}

      {/* Filter */}
      <div className="flex items-center gap-2">
        <Filter className="w-3.5 h-3.5 text-[#706E6B]" />
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="bg-[#FAFAFA] border border-[#C9C9C9] rounded-lg text-xs text-[#444444] px-2 py-1.5 outline-none"
          data-testid="change-status-filter"
        >
          {STATUS_FILTER_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-[#706E6B]" />
        </div>
      ) : changes.length === 0 ? (
        <EmptyState onRecord={() => setShowModal(true)} />
      ) : (
        <div className="space-y-2" data-testid="changes-list">
          {changes.map(c => (
            <ChangeRow
              key={c.id}
              change={c}
              onClick={() => navigate(`/events/${eventId}/changes/${c.id}`)}
            />
          ))}
        </div>
      )}

      {/* Record Change Modal */}
      {showModal && (
        <RecordChangeModal
          eventId={eventId}
          config={config}
          onClose={() => setShowModal(false)}
          onSuccess={() => { setShowModal(false); load(); }}
        />
      )}
    </div>
  );
}

// ── Change Row ─────────────────────────────────────────────────────────────────
function ChangeRow({ change, onClick }) {
  const icon = CHANGE_TYPE_ICON[change.change_type] || "📌";
  const severityStyle = SEVERITY_STYLE[change.severity] || SEVERITY_STYLE.LOW;
  const statusStyle = STATUS_STYLE[change.status] || STATUS_STYLE.PROPOSED;

  return (
    <button
      data-testid={`change-row-${change.change_number}`}
      onClick={onClick}
      className="w-full text-left bg-white border border-[#E5E5E5] rounded-xl p-4 hover:border-[#C9C9C9] hover:bg-[#FAFAFA] transition-all group"
    >
      <div className="flex items-start gap-3">
        <div className="text-lg flex-shrink-0 mt-0.5">{icon}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="font-mono text-xs text-[#706E6B]">{change.change_number}</span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${severityStyle}`}>
              {change.severity}
            </span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${statusStyle}`}>
              {change.status?.replace(/_/g, " ")}
            </span>
          </div>
          <p className="text-sm font-medium text-[#181818] truncate">{change.title}</p>
          <div className="flex items-center gap-3 mt-1.5 flex-wrap">
            <span className="text-[10px] text-[#706E6B]">
              {change.change_type?.replace(/_/g, " ")}
            </span>
            {change.requested_by && (
              <span className="text-[10px] text-[#706E6B]">· {change.requested_by}</span>
            )}
            <span className="text-[10px] text-[#706E6B]">
              · {change.created_at?.slice(0, 10)}
            </span>
          </div>
        </div>
        <ChevronRight className="w-4 h-4 text-[#706E6B] group-hover:text-[#706E6B] flex-shrink-0 mt-1" />
      </div>
    </button>
  );
}

// ── Empty State ────────────────────────────────────────────────────────────────
function EmptyState({ onRecord }) {
  return (
    <div className="text-center py-14 space-y-4" data-testid="changes-empty-state">
      <div className="w-14 h-14 rounded-2xl bg-[#FAFAFA] border border-[#C9C9C9] flex items-center justify-center mx-auto">
        <GitPullRequest className="w-7 h-7 text-[#706E6B]" />
      </div>
      <div>
        <p className="text-sm font-semibold text-[#444444]">No Changes Recorded</p>
        <p className="text-xs text-[#706E6B] mt-1">
          Record a change to begin cross-thread impact assessment.
        </p>
      </div>
      <button
        onClick={onRecord}
        className="inline-flex items-center gap-2 px-4 py-2 bg-[#EEF4FF] text-[#0176D3] border border-[#0176D3]/30 rounded-lg text-xs font-medium hover:bg-[#EEF4FF] transition-colors"
      >
        <Plus className="w-3.5 h-3.5" />
        Record First Change
      </button>
    </div>
  );
}

// ── Summary Chip ───────────────────────────────────────────────────────────────
function SummaryChip({ label, value, color }) {
  return (
    <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-white border border-[#E5E5E5] rounded-lg">
      <span className={`text-sm font-bold ${color}`}>{value}</span>
      <span className="text-xs text-[#706E6B]">{label}</span>
    </div>
  );
}

// ── Record Change Modal ────────────────────────────────────────────────────────
function RecordChangeModal({ eventId, config, onClose, onSuccess }) {
  const [form, setForm] = useState({
    change_type: "",
    title: "",
    reason: "",
    source_type: "CUSTOMER_REQUEST",
    previous_value: "",
    new_value: "",
    severity: "MEDIUM",
    effective_date: "",
    commercial_impact_possible: false,
  });
  const [saving, setSaving] = useState(false);

  const changeTypes = config?.change_types || [];
  const severityLevels = config?.severity_levels || ["LOW", "MEDIUM", "HIGH", "CRITICAL"];
  const sourceTypes = config?.source_types || ["CUSTOMER_REQUEST", "VENDOR_CONSTRAINT", "INTERNAL", "OTHER"];

  function f(k, v) { setForm(p => ({ ...p, [k]: v })); }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.change_type) return toast.error("Select a change type");
    if (!form.title.trim()) return toast.error("Provide a title");

    setSaving(true);
    try {
      // Convert previous_value / new_value to structured objects
      const payload = {
        ...form,
        previous_value: form.previous_value ? { value: form.previous_value } : {},
        new_value: form.new_value ? { value: form.new_value } : {},
      };
      await api.post(`/events/${eventId}/changes`, payload);
      toast.success("Change recorded");
      onSuccess();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to record change");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-4">
      <div
        className="bg-white border border-[#C9C9C9] rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        data-testid="record-change-modal"
      >
        <div className="sticky top-0 bg-white border-b border-[#E5E5E5] px-5 py-4 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-[#181818]">Record Change</h3>
            <p className="text-xs text-[#706E6B] mt-0.5">Capture a change for impact assessment</p>
          </div>
          <button onClick={onClose} className="text-[#706E6B] hover:text-[#181818] text-xl leading-none">×</button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {/* Change Type */}
          <div>
            <label className="block text-xs text-[#706E6B] mb-1.5">Change Type *</label>
            <select
              value={form.change_type}
              onChange={e => {
                f("change_type", e.target.value);
                if (!form.title) {
                  f("title", e.target.value.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase()) + " Change");
                }
              }}
              required
              className="w-full bg-[#FAFAFA] border border-[#C9C9C9] rounded-lg text-sm text-[#181818] px-3 py-2 outline-none focus:border-[#0176D3]"
              data-testid="change-type-select"
            >
              <option value="">Select change type</option>
              {changeTypes.map(t => (
                <option key={t} value={t}>{t.replace(/_/g, " ")}</option>
              ))}
            </select>
          </div>

          {/* Title */}
          <div>
            <label className="block text-xs text-[#706E6B] mb-1.5">Title *</label>
            <input
              value={form.title}
              onChange={e => f("title", e.target.value)}
              required
              placeholder="e.g. Guest count increased from 800 to 950"
              className="w-full bg-[#FAFAFA] border border-[#C9C9C9] rounded-lg text-sm text-[#181818] px-3 py-2 outline-none focus:border-[#0176D3]"
              data-testid="change-title-input"
            />
          </div>

          {/* Previous / New Value */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-[#706E6B] mb-1.5">Previous Value</label>
              <input
                value={form.previous_value}
                onChange={e => f("previous_value", e.target.value)}
                placeholder="e.g. 800"
                className="w-full bg-[#FAFAFA] border border-[#C9C9C9] rounded-lg text-sm text-[#181818] px-3 py-2 outline-none focus:border-[#0176D3]"
                data-testid="change-prev-value-input"
              />
            </div>
            <div>
              <label className="block text-xs text-[#706E6B] mb-1.5">New Value</label>
              <input
                value={form.new_value}
                onChange={e => f("new_value", e.target.value)}
                placeholder="e.g. 950"
                className="w-full bg-[#FAFAFA] border border-[#C9C9C9] rounded-lg text-sm text-[#181818] px-3 py-2 outline-none focus:border-[#0176D3]"
                data-testid="change-new-value-input"
              />
            </div>
          </div>

          {/* Reason */}
          <div>
            <label className="block text-xs text-[#706E6B] mb-1.5">Reason</label>
            <textarea
              value={form.reason}
              onChange={e => f("reason", e.target.value)}
              rows={2}
              placeholder="Why is this change being made?"
              className="w-full bg-[#FAFAFA] border border-[#C9C9C9] rounded-lg text-sm text-[#181818] px-3 py-2 outline-none resize-none focus:border-[#0176D3]"
              data-testid="change-reason-input"
            />
          </div>

          {/* Source / Severity / Effective Date */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-[#706E6B] mb-1.5">Source</label>
              <select
                value={form.source_type}
                onChange={e => f("source_type", e.target.value)}
                className="w-full bg-[#FAFAFA] border border-[#C9C9C9] rounded-lg text-sm text-[#181818] px-3 py-2 outline-none focus:border-[#0176D3]"
                data-testid="change-source-select"
              >
                {sourceTypes.map(s => (
                  <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-[#706E6B] mb-1.5">Severity</label>
              <select
                value={form.severity}
                onChange={e => f("severity", e.target.value)}
                className="w-full bg-[#FAFAFA] border border-[#C9C9C9] rounded-lg text-sm text-[#181818] px-3 py-2 outline-none focus:border-[#0176D3]"
                data-testid="change-severity-select"
              >
                {severityLevels.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs text-[#706E6B] mb-1.5">Effective Date</label>
            <input
              type="date"
              value={form.effective_date}
              onChange={e => f("effective_date", e.target.value)}
              className="w-full bg-[#FAFAFA] border border-[#C9C9C9] rounded-lg text-sm text-[#181818] px-3 py-2 outline-none focus:border-[#0176D3]"
              data-testid="change-effective-date-input"
            />
          </div>

          {/* Commercial flag */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.commercial_impact_possible}
              onChange={e => f("commercial_impact_possible", e.target.checked)}
              className="rounded border-[#C9C9C9]"
              data-testid="change-commercial-flag"
            />
            <span className="text-xs text-[#706E6B]">Commercial impact review required</span>
          </label>

          {/* Actions */}
          <div className="flex gap-3 pt-2 border-t border-[#E5E5E5]">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 text-xs font-medium bg-[#FAFAFA] text-[#444444] rounded-lg hover:bg-[#E5E5E5] transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 py-2 text-xs font-semibold bg-[#0176D3] text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="change-submit-btn"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
              Record Change
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
